import type { useTranslate } from "@/lib/i18n";
import type { BillDiscountKind, Invoice } from "@/lib/api/types";
import { liveOpenTotals } from "./groupPlates";
import {
  foodBaseAfterDiscount,
  invoiceChargeRates,
  netFoodOf,
  previewBillDiscount,
  roundMoney,
} from "./pricing";

/**
 * D146 — A DISCOUNT AT THE BILL («الخصم»), the till's side: the closed reason
 * list, what the sheet sends, and the figures it previews. The server decides
 * every one of them again (`bill-discount.policy.ts`).
 */

/** The closed list, mirrored from the server's `BILL_DISCOUNT_REASON_CODES`. */
export const BILL_DISCOUNT_REASON_CODES = [
  "ROUNDING",
  "COMPLAINT",
  "REGULAR",
  "OWNER_GUEST",
  "OTHER",
] as const;
export type BillDiscountReasonCode =
  (typeof BILL_DISCOUNT_REASON_CODES)[number];

type Translate = ReturnType<typeof useTranslate>;

/**
 * A reason code in words. A code outside the list (a row written by a newer
 * server) prints as itself rather than as nothing.
 */
export function billDiscountReasonLabel(t: Translate, code: string): string {
  switch (code) {
    case "ROUNDING":
      return t((m) => m.billDiscount.reasonRounding);
    case "COMPLAINT":
      return t((m) => m.billDiscount.reasonComplaint);
    case "REGULAR":
      return t((m) => m.billDiscount.reasonRegular);
    case "OWNER_GUEST":
      return t((m) => m.billDiscount.reasonOwnerGuest);
    case "OTHER":
      return t((m) => m.billDiscount.reasonOther);
    default:
      return code;
  }
}

/** The three quick chips the cashier asked for. */
export const BILL_DISCOUNT_PERCENT_CHIPS = [5, 10, 15] as const;

/** The store cap when an older API sends none (the server's own default). */
export const DEFAULT_BILL_DISCOUNT_MAX_PCT = 50;

/** What the sheet is holding: one kind at a time, as typed. */
export type BillDiscountDraft = {
  kind: BillDiscountKind;
  /** The raw text of the figure (a chip writes its percent here). */
  raw: string;
  reasonCode: BillDiscountReasonCode | null;
  note: string;
};

/** The `applyBillDiscount` payload, exactly as the command contract states it. */
export type ApplyBillDiscountPayload = {
  kind: BillDiscountKind;
  value: number;
  reasonCode: BillDiscountReasonCode;
  reasonNote?: string;
};

/** The figure typed, as money, or null when it is not one. */
export function draftValue(draft: BillDiscountDraft): number | null {
  const text = draft.raw.trim();
  if (text === "") return null;
  const value = Number.parseFloat(text);
  if (!Number.isFinite(value) || value <= 0) return null;
  return roundMoney(value);
}

/** Whether OTHER is picked without the note it needs (D43's rule). */
export function noteMissing(draft: BillDiscountDraft): boolean {
  return draft.reasonCode === "OTHER" && draft.note.trim() === "";
}

/**
 * The payload to send, or null while the draft cannot be sent: no figure, no
 * reason, or OTHER without its note. The note rides only when written.
 */
export function billDiscountPayload(
  draft: BillDiscountDraft,
): ApplyBillDiscountPayload | null {
  const value = draftValue(draft);
  if (value === null || draft.reasonCode === null || noteMissing(draft)) {
    return null;
  }
  const note = draft.note.trim();
  return {
    kind: draft.kind,
    value,
    reasonCode: draft.reasonCode,
    ...(note ? { reasonNote: note } : {}),
  };
}

/**
 * The sheet's preview: the bill as it stands WITHOUT any discount (the new one
 * replaces the old, it does not stack), and what the draft would make of it.
 * Fired plates only — the basket is not on the bill the server prices.
 */
export function billDiscountPreview(
  invoice: Invoice,
  draft: BillDiscountDraft,
  capPct: number,
) {
  const undiscounted = liveOpenTotals({ ...invoice, billDiscount: null });
  const feeTotal = roundMoney(
    (invoice.deliveryFee ?? 0) + (invoice.deliveryFeeVat ?? 0),
  );
  const preview = previewBillDiscount({
    kind: draft.kind,
    value: draftValue(draft) ?? 0,
    subtotal: undiscounted.subtotal,
    charges: invoiceChargeRates(invoice),
    feeTotal,
    capPct,
    tendered: invoice.tenderedTotal ?? 0,
  });
  return { before: undiscounted.total, ...preview };
}

/**
 * The line a STORED discount prints on a closed bill's paper — the net in
 * exclusive mode, its tax-inclusive equivalent in inclusive mode — so
 * `subtotal − line (+ charges) = total` holds on the receipt.
 */
export function storedBillDiscountLine(
  invoice: Pick<
    Invoice,
    | "subtotal"
    | "billDiscount"
    | "serviceChargePercent"
    | "vatPercent"
    | "vatOnServiceCharge"
    | "pricesIncludeTax"
  >,
): number {
  const amount = invoice.billDiscount?.amount ?? 0;
  if (!(amount > 0)) return 0;
  return roundMoney(
    invoice.subtotal -
      foodBaseAfterDiscount(invoice.subtotal, invoiceChargeRates(invoice), amount),
  );
}

/**
 * The bill's discount line as it stands: live on an OPEN bill (a PERCENT
 * tracks the plates), stored on every other. 0 when there is none.
 */
export function billDiscountLine(invoice: Invoice): number {
  if (!invoice.billDiscount) return 0;
  return invoice.status === "OPEN"
    ? liveOpenTotals(invoice).billDiscountGross
    : storedBillDiscountLine(invoice);
}

/**
 * A BILL FOR SOME OF ITS PLATES, discounted by the ONE rule (D146): the whole
 * bill's single factor `discount ÷ net`, applied to the subset's own net with
 * the shared money rounder. Carried as an equivalent PERCENT so the subset
 * prices through the same pricing mirror; a bill without a discount is
 * returned untouched.
 */
export function proRatedBillDiscount(
  invoice: Invoice,
): Invoice["billDiscount"] {
  const discount = invoice.billDiscount;
  if (!discount) return discount ?? null;
  const whole = liveOpenTotals(invoice);
  const wholeNet = netFoodOf(whole.subtotal, invoiceChargeRates(invoice));
  if (!(wholeNet > 0) || !(whole.billDiscountTotal > 0)) return null;
  return {
    ...discount,
    kind: "PERCENT",
    value: (whole.billDiscountTotal / wholeNet) * 100,
    // The factor is already within the cap the whole bill was clamped to.
    capPct: 100,
  };
}

